import type { SyncStatus } from '@/features/sync/useSync'
import { syncDot } from './syncDot'

interface MoreMenuProps {
  syncStatus: SyncStatus
  onSync: () => void
  onBackup: () => void
  onManage: () => void
  onStatistics: () => void
  onTrend: () => void
  onCalendar: () => void
  onGoals: () => void
  onStampers: () => void
  onSettings: () => void
  onClose: () => void
}

const SYNC_TEXT: Record<SyncStatus, string> = {
  idle: 'Up to date',
  syncing: 'Syncing…',
  offline: 'Offline',
  error: 'Sync failed',
  unauthorized: 'Token rejected',
}

/**
 * Overflow menu for the title bar.
 *
 * A phone title bar fits about four controls at a comfortable 44px each. Sync
 * and Backup were competing with undo, redo and Now for that space, which
 * squeezed everything below the touch-target minimum and pushed the last
 * button off screen entirely. They live here instead, where they can carry
 * labels rather than being guessed from a glyph.
 */
export function MoreMenu({
  syncStatus,
  onSync,
  onBackup,
  onManage,
  onStatistics,
  onTrend,
  onCalendar,
  onGoals,
  onStampers,
  onSettings,
  onClose,
}: MoreMenuProps) {
  return (
    <>
      <div className="ctx-scrim" onPointerDown={onClose} aria-hidden="true" />
      <div className="more-menu" role="menu">
        <button className="more-item" role="menuitem" onClick={onCalendar}>
          <span className="more-glyph" aria-hidden="true">
            ▦
          </span>
          <span className="more-label">Calendar</span>
        </button>

        <button className="more-item" role="menuitem" onClick={onStatistics}>
          <span className="more-glyph" aria-hidden="true">
            ◕
          </span>
          <span className="more-label">Statistics</span>
        </button>

        <button className="more-item" role="menuitem" onClick={onTrend}>
          <span className="more-glyph" aria-hidden="true">
            ⌁
          </span>
          <span className="more-label">Trend</span>
        </button>

        <button className="more-item" role="menuitem" onClick={onGoals}>
          <span className="more-glyph" aria-hidden="true">
            ◎
          </span>
          <span className="more-label">Goals</span>
        </button>

        <button className="more-item" role="menuitem" onClick={onStampers}>
          <span className="more-glyph" aria-hidden="true">
            ✦
          </span>
          <span className="more-label">Stampers</span>
        </button>

        <button className="more-item" role="menuitem" onClick={onManage}>
          <span className="more-glyph" aria-hidden="true">
            ☰
          </span>
          <span className="more-label">Event types</span>
        </button>

        <button className="more-item" role="menuitem" onClick={onSettings}>
          <span className="more-glyph" aria-hidden="true">
            ⚙
          </span>
          <span className="more-label">Settings</span>
        </button>

        <button className="more-item" role="menuitem" onClick={onSync}>
          <span className={`dot dot--${syncDot(syncStatus)}`} aria-hidden="true" />
          <span className="more-label">Sync</span>
          <span className="more-detail">{SYNC_TEXT[syncStatus]}</span>
        </button>

        <button className="more-item" role="menuitem" onClick={onBackup}>
          <span className="more-glyph" aria-hidden="true">
            ⭳
          </span>
          <span className="more-label">Backup &amp; export</span>
        </button>
      </div>
    </>
  )
}
